import { useState } from "react";

import { createFileRoute } from "@tanstack/react-router";

import type { OrganisationRecord } from "../api/hooks";
import { useOrganisationRecords, useUnlinkedAccounts } from "../api/hooks";
import { useDebouncedValue } from "../lib/useDebouncedValue";

import { AccountLinkPanel } from "./-AccountLinkPanel";
import { OrganisationForm } from "./-OrganisationForm";

import styles from "./organisations.module.scss";

// Which half of the table an operator is looking at. "Everything" is the whole
// list; the other two are the two jobs - what still needs pointing at a
// customer, and what already is one.
type LinkedFilter = "all" | "linked" | "unlinked";

const linkedFilters: { value: LinkedFilter; label: string }[] = [
  { value: "all", label: "Everything" },
  { value: "unlinked", label: "Not linked" },
  { value: "linked", label: "Customers" },
];

function linkedParameter(filter: LinkedFilter): boolean | undefined {
  if (filter === "all") return undefined;

  return filter === "linked";
}

// The size a body claims about itself, which is not the mapped area a customer's
// sites add up to. Blank when nobody has found out, because zero would read as
// "no land" - a claim nobody has made.
//
// Anything under a hectare reads as "<1 ha" for the same reason: a small urban
// trust with 0.4 ha is a real record an operator is triaging, and rounding it
// would print the very string the null case exists to avoid.
function hectaresLabel(hectares: number | null): string {
  if (hectares === null) return "";
  if (hectares > 0 && hectares < 1) return "<1 ha";

  return `${Math.round(hectares).toLocaleString("en-GB")} ha`;
}

function RecordRow({
  record,
  onEdit,
}: {
  record: OrganisationRecord;
  onEdit: (record: OrganisationRecord) => void;
}) {
  return (
    <tr>
      <td className="tbl-org">
        {/* A button rather than a click handler on the row: the row is not
            focusable, and an operator on a keyboard has to be able to open the
            same thing a pointer can. */}
        <button type="button" className={styles.rowName} onClick={() => onEdit(record)}>
          {record.name}
        </button>
        {record.kind !== "" && <div className={styles.kind}>{record.kind}</div>}
      </td>
      <td>{record.county}</td>
      <td className="tbl-right">{hectaresLabel(record.hectares)}</td>
      <td>
        {record.websiteDomain !== "" && (
          <span className={styles.domain}>{record.websiteDomain}</span>
        )}
      </td>
      <td>
        {record.organisationId === null ? (
          <span className="badge badge-muted">Not linked</span>
        ) : (
          <span className="badge badge-verified">Customer</span>
        )}
      </td>
    </tr>
  );
}

function Organisations() {
  const [search, setSearch] = useState("");
  const [linkedFilter, setLinkedFilter] = useState<LinkedFilter>("all");

  // Null is closed. A record edits it; the "adding" sentinel opens an empty
  // form, which is distinguishable from closed in a way that a boolean plus a
  // record would not be.
  const [editing, setEditing] = useState<OrganisationRecord | "adding" | null>(null);

  // The linking queue is a second job on this page rather than a second page:
  // the answer to "which record is this account" is in the list behind it.
  const [linkOffset, setLinkOffset] = useState(0);
  const [linking, setLinking] = useState<{ id: string; name: string } | null>(null);
  const { data: unlinked, isError: unlinkedFailed } = useUnlinkedAccounts(linkOffset);

  // The banner shows a few at a time; the API sends a sitting's worth. Skipping
  // has to move past what an operator actually saw, or the ones between the
  // third and the twenty-fifth become unreachable from any control on the page.
  const queueShown = unlinked?.items.slice(0, 3) ?? [];

  // The box stays on the raw value so typing is never laggy; the query follows
  // the settled one. Without this every keystroke is a request and a cache
  // entry, and keepPreviousData hides that from whoever is typing.
  const searchTerm = useDebouncedValue(search.trim(), 250);

  const {
    data,
    isPending,
    isError,
    isPlaceholderData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useOrganisationRecords({
    search: searchTerm === "" ? undefined : searchTerm,
    linked: linkedParameter(linkedFilter),
  });

  const records = data?.pages.flatMap((page) => page.items) ?? [];
  // The API counts the whole filtered set rather than the loaded pages, so this
  // is a real total.
  const total = data?.pages[0]?.count;

  return (
    <>
      <div className={styles.header}>
        <div>
          <div className="page-title">Organisations</div>
          <div className="page-subtitle">
            Every organisation we know about, whether or not they are a customer
          </div>
        </div>
        <div className={styles.headerRight}>
          {total !== undefined && (
            <div className={styles.headerCount} aria-live="polite">
              {total.toLocaleString("en-GB")}{" "}
              {total === 1 ? "organisation" : "organisations"}
            </div>
          )}
          <button
            type="button"
            className={styles.add}
            onClick={() => setEditing("adding")}
          >
            Add an organisation
          </button>
        </div>
      </div>

      {unlinkedFailed && (
        <div className={styles.queue} role="alert">
          <span className={styles.queueCount}>
            The list of customers waiting for a record could not be loaded.
          </span>
        </div>
      )}

      {unlinked !== undefined && unlinked.total > 0 && (
        <div className={styles.queue}>
          <span className={styles.queueCount}>
            {unlinked.total.toLocaleString("en-GB")}{" "}
            {unlinked.total === 1 ? "customer has" : "customers have"} no record yet
          </span>
          {queueShown.map((account) => (
            <button
              key={account.id}
              type="button"
              className={styles.queueItem}
              onClick={() => setLinking(account)}
            >
              {account.name}
            </button>
          ))}
          {unlinked.items.length === 0 && linkOffset > 0 && (
            // The offset outlived the rows it pointed past, which happens by
            // linking them. Sending someone back to the top is the only honest
            // move; the count above still says what is waiting there.
            <button
              type="button"
              className={styles.queueItem}
              onClick={() => setLinkOffset(0)}
            >
              Back to the start of the queue
            </button>
          )}
          {/* total is the whole queue and does not shrink as the offset moves,
              so it cannot answer "is there anything past what I am showing"
              on its own. Without the offset in the sum the button appears at
              the tail and walks the operator off the end, then keeps
              appearing there doing nothing. */}
          {unlinked.total > linkOffset + queueShown.length && (
            <button
              type="button"
              className={styles.queueItem}
              onClick={() => setLinkOffset(linkOffset + queueShown.length)}
            >
              Skip these
            </button>
          )}
        </div>
      )}

      <div className={styles.controls}>
        <input
          type="search"
          className={styles.search}
          placeholder="Search by name"
          aria-label="Search organisations by name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        <div className={styles.filters} role="group" aria-label="Filter by customer status">
          {linkedFilters.map((filter) => (
            <button
              key={filter.value}
              type="button"
              className={styles.pill}
              aria-pressed={linkedFilter === filter.value}
              onClick={() => setLinkedFilter(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="panel" aria-busy={isPlaceholderData}>
        {isPending && (
          <div className={styles.state} role="status">
            Loading organisations…
          </div>
        )}

        {isError && (
          <div className={styles.state} role="alert">
            The organisations could not be loaded.
          </div>
        )}

        {!isPending && !isError && records.length === 0 && (
          <div className={styles.state} role="status">
            {searchTerm === "" && linkedFilter === "all"
              ? "No organisations yet."
              : "Nothing matches that."}
          </div>
        )}

        {records.length > 0 && (
          <table className="tbl" aria-label="Organisations">
            <thead>
              <tr>
                <th>Organisation</th>
                <th>County</th>
                <th className="tbl-right">Size</th>
                <th>Website</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <RecordRow
                  key={record.id}
                  record={record}
                  onEdit={(edited) => setEditing(edited)}
                />
              ))}
            </tbody>
          </table>
        )}

        {hasNextPage && (
          <div className={styles.foot}>
            {total !== undefined && (
              <span className={styles.footNote}>
                {records.length.toLocaleString("en-GB")} of{" "}
                {total.toLocaleString("en-GB")} shown
              </span>
            )}
            <button
              type="button"
              className={styles.loadMore}
              disabled={isFetchingNextPage}
              onClick={() => fetchNextPage()}
            >
              {isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>

      {linking !== null && (
        <AccountLinkPanel
          key={linking.id}
          account={linking}
          onClose={() => setLinking(null)}
          onLinked={() => setLinking(null)}
        />
      )}

      {editing !== null && (
        <OrganisationForm
          // Keyed so switching records remounts the form. Its field state is
          // initialised once from the record, while the id it saves to follows
          // the prop - so without this, opening one record and then another
          // (reachable by Tab, since the scrim only stops a pointer) writes the
          // first one's values over the second.
          key={editing === "adding" ? "adding" : editing.id}
          record={editing === "adding" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

export const Route = createFileRoute("/organisations")({
  component: Organisations,
});
